import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import * as Sentry from '@sentry/react';
import i18n from '@/i18n';
import type {
  APBillIndexRowWire,
  APBillIndexResponse,
  APBillDetailResponse,
  APBillListResponse,
  APBillEditContextResponse,
  APBillWire,
  NlWkrCategory,
} from '@releve/shared';
import { api, unwrap, authedGet, authedPost, authedPatch, authedDelete, type EnvelopeError } from '@/lib/api';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { addBreadcrumb } from '@/lib/sentry';
import { safeFormatDate } from '@/lib/dateUtils';
import { filterPaymentTransactions, isFeedLinkedPayment, voidedPayments } from '@/lib/invoiceUtils';
import { useToast } from '@/hooks/useToast';
import { APInvoiceStatus } from '@/types/financial-accounts';
import type { TaxTreatment } from '@/lib/taxCalculations';
import { trackEvent } from '@/lib/posthog';
import { toastTaxRateRefusal } from '@/lib/taxRateRefusal';
import { invalidateProjectMoney } from "@/hooks/useProjectMoneySummary";

/**
 * Base shape returned by AP mutation RPCs (verified via `pg_get_functiondef`
 * on the live DB). Only `success` and `error` are universally present —
 * individual RPCs additionally return `transaction_id`, `transaction_number`,
 * `message`, etc., but no current consumer reads those fields.
 */
/**
 * `error_code` approve_ap_invoice returns when a live payment on the bill
 * sits on a liability account other than the bill's own (metadata.ap_account_id).
 * The approve route returns that envelope as a coded 400 body, so the code
 * rides on the thrown error's `envelope` (see `isPaymentAccountMismatch`).
 */
export const PAYMENT_ACCOUNT_MISMATCH = 'PAYMENT_ACCOUNT_MISMATCH';

/** True when an approve call was refused with PAYMENT_ACCOUNT_MISMATCH. */
export function isPaymentAccountMismatch(error: unknown): boolean {
  const envelope = (error as { envelope?: { code?: string; error_code?: string } } | null | undefined)?.envelope;
  return envelope?.code === PAYMENT_ACCOUNT_MISMATCH || envelope?.error_code === PAYMENT_ACCOUNT_MISMATCH;
}

/**
 * Whether any of the bill's payments came from the bank feed. Such a payment
 * is undone from the feed, never reversed here, so `repoint_ap_bill_payments`
 * refuses the whole bill (IMPORT_LINKED). Three link mechanisms — see the
 * `payment_import_matches` note on `APInvoice`; the third is the dominant one
 * ("pay a bill from the feed") and missing it gave the edit flow a false
 * negative that could leave a half-edited bill.
 */
export function hasFeedLinkedPayment(
  invoice: Pick<APInvoice, 'payment_transactions'> | null | undefined,
): boolean {
  return (invoice?.payment_transactions ?? []).some(isFeedLinkedPayment);
}

export interface APInvoiceMutationResult {
  success: boolean;
  error?: string;
  /**
   * The ledger transaction id returned by payment/create RPCs
   * (`process_ap_payment` returns it as `transaction_id`). Used by the
   * bank-feed "Pay a bill" flow to link the created payment txn back to the
   * import. Absent on RPCs that don't create a transaction.
   */
  transaction_id?: string;
  /** create_ap_bill_with_line_items returns the new bill id here. */
  invoice_id?: string;
}

export interface APInvoiceLineItem {
  id: string;
  invoice_id: string;
  expense_account_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_rate_id?: string | null;
  tax_treatment: TaxTreatment;
  tax_inclusive: boolean;
  tax_amount?: number;
  line_total?: number;
  line_total_with_tax?: number;
  sort_order?: number;
  exemption_reason?: string;
  exemption_reason_other?: string;
}

export interface APInvoice {
  id: string;
  organization_id: string;
  project_id: string | null;
  business_id: string | null;
  person_id: string | null;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  amount_total: number;
  amount_paid: number;
  amount_remaining: number; // Generated column: amount_total - amount_paid
  description: string;
  purchase_order_number: string | null;
  payment_terms: string | null;
  approval_status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'on_hold' | null;
  payment_status: APInvoiceStatus;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  void_transaction_id: string | null;
  metadata: Record<string, unknown> | null;
  self_billing?: boolean;
  additional_vat_notes?: string;
  created_by: string;
  ledger_transaction_id: string | null;
  created_at: string;
  updated_at: string;
  // Line items
  line_items?: APInvoiceLineItem[];
  // Joined data
  business?: {
    name: string;
  };
  person?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  project?: {
    id: string;
    name: string;
    code: string;
  } | null;
  ledger_transaction?: {
    transaction_number: string;
    project_id: string | null;
    fund_id: string | null;
    reference?: string | null;
    matched_imports?: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
    created_imports?: Array<{ id: string; booking_date: string; description: string | null }>;
    entries?: Array<{
      id: string;
      debit_amount: number;
      credit_amount: number;
      description: string;
      chart_of_accounts?: {
        id: string;
        account_name: string;
        account_type?: { type_name: string | null } | null;
      };
    }>;
  };
  payment_transactions?: Array<{
    id: string;
    transaction_number: string;
    transaction_date: string;
    description: string;
    total_amount: number;
    reference?: string;
    fund_id?: string;
    /**
     * Set when this journal is a VOID reversing another one. Non-payment
     * journals (voids, and the postings they reverse) are stripped by
     * `filterPaymentTransactions` before this array reaches the UI.
     */
    void_of_transaction_id?: string | null;
    matched_imports?: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
    created_imports?: Array<{ id: string; booking_date: string; description: string | null }>;
    /**
     * Bank-feed matches where THIS journal is the payment_transaction_id — the
     * "pay a bill from the feed" link. It is the third import-link mechanism
     * (alongside matched_imports/created_imports) and the dominant one for AP
     * payments; without it the edit flow's import-linked pre-check has a false
     * negative and can leave a half-edited bill. See `hasImportLinkedPayment`.
     */
    payment_import_matches?: Array<{ id: string }>;
    entries?: Array<{
      id: string;
      debit_amount: number;
      credit_amount: number;
      description: string;
      chart_of_accounts?: {
        id: string;
        account_name: string;
        account_type?: { type_name: string | null } | null;
      };
    }>;
  }>;
  /**
   * Undone payments: each reversed payment paired with the reversal that
   * cancels it, derived by the hook from the RAW endpoint payload (via
   * `voidedPayments`) — which is why the read endpoints return
   * `payment_transactions` UNFILTERED: `filterPaymentTransactions` strips
   * both halves of every reversal pair, so computing this after (or server-
   * side of) the narrowing would always yield []. Drives the bill's
   * "Voided payments" audit section.
   */
  voided_payments?: Array<{
    payment: NonNullable<APInvoice['payment_transactions']>[number];
    reversal: NonNullable<APInvoice['payment_transactions']>[number];
  }>;
  void_transaction?: {
    id: string;
    transaction_number: string;
    transaction_date?: string;
    description?: string;
    total_amount?: number;
    entries?: Array<{
      id: string;
      debit_amount: number;
      credit_amount: number;
      description: string;
      chart_of_accounts?: {
        id: string;
        account_name: string;
        account_type?: { type_name: string | null } | null;
      };
    }>;
  };
}

export interface CreateAPInvoiceData {
  business_id?: string;
  person_id?: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  /**
   * Accepted for caller compatibility but NOT transmitted —
   * create_ap_bill_with_line_items computes the total server-side as the
   * sign-aware sum of the stored generated line totals (the same expression
   * approve_ap_invoice heals with).
   */
  amount_total: number;
  description: string;
  purchase_order_number?: string;
  project_id?: string;
  ap_account_id: string;
  fund_id?: string;
  line_items: Array<{
    expense_account_id: string;
    description: string;
    quantity: number;
    unit_price: number;
    tax_rate: number;
    tax_rate_id?: string;
    tax_treatment?: TaxTreatment;
    tax_inclusive?: boolean;
    sort_order: number;
    exemption_reason?: string;
    exemption_reason_other?: string;
    /** NL WKR classification (jurisdiction-gated in the UI). */
    wkr_category?: NlWkrCategory;
  }>;
}

interface ProcessAPPaymentData {
  ap_invoice_id: string;
  payment_amount: number;
  payment_date: string;
  payment_method: string;
  reference?: string;
  bank_account_id: string;
  ap_account_id: string;
  project_id?: string;
  fund_id?: string;
}

/** Shared line-item body shape for the create/update endpoints. */
const toLineItemBody = (item: CreateAPInvoiceData['line_items'][number]) => ({
  expense_account_id: item.expense_account_id,
  description: item.description,
  quantity: item.quantity,
  unit_price: item.unit_price,
  tax_rate: item.tax_rate ?? 0,
  tax_rate_id: item.tax_rate_id || null,
  tax_treatment: item.tax_treatment || 'standard',
  tax_inclusive: item.tax_inclusive ?? false,
  sort_order: item.sort_order,
  exemption_reason: item.exemption_reason || null,
  exemption_reason_other: item.exemption_reason_other || null,
  // NL WKR: must survive this whitelist or AP classifications silently
  // vanish (undefined drops out of JSON; the wire schema takes the enum only).
  wkr_category: item.wkr_category || undefined,
});

/**
 * Cross-domain caches every AP mutation must refresh. (The org-wide AP aging
 * query this used to invalidate is gone — the per-account aging on the
 * account page reads the `['accountsPayable', orgId]` index prefix, which
 * every AP mutation already invalidates.)
 */
export const invalidateApAging = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId?: string,
) => {
  // NL WKR totals/index change on AP create/update/delete (bridge sync) AND on
  // void (read-side is_voided exclusion). Org-scoped when the caller has the
  // org id (review fix — the bare prefix refetched every visited org's WKR
  // caches); callers without one fall back to a blanket prefix. No-op for
  // non-NL orgs either way.
  void queryClient.invalidateQueries({
    queryKey: organizationId ? ['nl-wkr', organizationId] : ['nl-wkr'],
  });
};

/** One row of the slim AP bill index (the `ap_bill_index` DB view). */
export type APBillIndexRow = APBillIndexRowWire;

/**
 * The COMPLETE slim AP bill index for an org — the API pages the
 * `ap_bill_index` view past PostgREST's 1,000-row cap, so it is never
 * silently truncated. Powers the bills grid's client-side search/sort/filter
 * and the accounting-overview compact tile; heavy detail (line items,
 * journals, payment history) is fetched lazily per row via `useAPInvoiceById`.
 *
 * Liveness contract: `entity: 'accountsPayable'` puts the key under the
 * `['accountsPayable', orgId]` prefix that EVERY AP mutation in this file
 * already invalidates (create/update/approve/reject/cancel/hold/resume/pay/
 * undo/repoint/delete), as do the cross-domain flows the index's
 * transaction-sourced fields depend on (useClearingActions,
 * useLedgerTransactions incl. useUpdateLedgerEntryAccount,
 * useRemoveImportBatch, useApplyTransactionToInvoice,
 * useLinkToAccountingImport). The domain has three names — `ap-invoices`
 * routes, `ap_bill_*` RPCs, `accountsPayable` query prefix — and the PREFIX
 * is the only load-bearing one: "tidying" it to 'apBills' would silently
 * kill every invalidation. `staleTime` (60s) only suppresses redundant
 * mount/focus refetches between mutations.
 */
export const useAPBillIndex = (
  organizationId?: string,
  options?: { projectId?: string; businessId?: string; personId?: string },
) => {
  // Normalized 3-key shape — a stable key object prevents cache
  // fragmentation between surfaces that omit different options.
  const normalized = {
    projectId: options?.projectId ?? null,
    businessId: options?.businessId ?? null,
    personId: options?.personId ?? null,
  };
  // Key shape, enabled gate, {items} unwrap, and the same-org placeholder
  // guard (no cross-org flash) all live in indexQueryOptions.
  return useQuery(
    indexQueryOptions<APBillIndexRowWire>({
      entity: 'accountsPayable',
      organizationId,
      path: `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/index`,
      params: normalized,
    }),
  );
};

/** Narrow a wire bill (raw payments) into the hook's public APInvoice shape. */
const toAPInvoice = (invoice: APBillWire): APInvoice => ({
  ...(invoice as unknown as APInvoice),
  // Derive the undone-payment pairs from the RAW array FIRST — the filter
  // below strips both the reversal and the reversed payment.
  voided_payments: voidedPayments(
    invoice.payment_transactions as APInvoice['payment_transactions'],
    invoice.ledger_transaction_id,
    { requireBankLeg: true },
  ),
  payment_transactions: filterPaymentTransactions(
    // void_of_transaction_id is load-bearing for the filter — keep it in the
    // cast so dropping it from the ENDPOINT select is a type error here, not
    // silence.
    invoice.payment_transactions as Array<{ id: string; void_of_transaction_id?: string | null }> | null,
    invoice.ledger_transaction_id,
  ) as APInvoice['payment_transactions'],
});

/**
 * INTERIM fat bill list — the pre-Hono embed shape, transport-swapped onto
 * GET /api/v1/ap-invoices (keyset-paged server-side past the 1,000-row cap
 * that silently truncated the old direct query). Its two remaining consumers
 * (ApplyToInvoiceDialog, AccountOverviewTab) want narrower reads — an
 * endpoint-search picker and a reporting RPC respectively; this hook and its
 * endpoint are deleted when they move. The bills GRID reads useAPBillIndex.
 */
export const useAccountsPayable = (organizationId?: string) => {
  return useQuery({
    queryKey: ['accountsPayable', organizationId, 'list'],
    queryFn: async () => {
      if (!organizationId) return [];
      const result = await authedGet<APBillListResponse>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices`,
      );
      return result.items.map(toAPInvoice);
    },
    enabled: !!organizationId,
  });
};

/**
 * The exact wire body `POST /api/v1/ap-invoices/orgs/:orgId/ap-invoices`
 * receives (`createAPBillSchema` in @releve/shared). ONE function feeds both
 * the create mutation below and the recurring-template payload
 * (`buildRecurringPayload('ap_bill', body)` strips `invoice_date` +
 * `due_date`, keeps `invoice_number`), so a key added here reaches the
 * scheduler's replayed payload the moment it lands. Pure — the `|| null`
 * coercions are the schema's nullable-uuid contract, not defaults.
 */
export const buildAPBillCreateBody = (data: CreateAPInvoiceData) => ({
  invoice_number: data.invoice_number,
  invoice_date: data.invoice_date,
  due_date: data.due_date,
  description: data.description || null,
  ap_account_id: data.ap_account_id,
  line_items: data.line_items.map(toLineItemBody),
  business_id: data.business_id || null,
  person_id: data.person_id || null,
  project_id: data.project_id || null,
  fund_id: data.fund_id || null,
  purchase_order_number: data.purchase_order_number || null,
});

export type APBillCreateBody = ReturnType<typeof buildAPBillCreateBody>;

/**
 * Every cache a NEW bill changes. Shared by `useCreateAPInvoice` and the
 * recurring "Run now" mutation (`invalidateGeneratedDocumentCaches`), which
 * creates bills without going through this hook.
 */
export const invalidateAPBillCreateCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) => {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // Per-account register (bank overview) — a payment/reversal/delete posts to
  // (or removes journals from) accounts, so the register's rows + running
  // balance must refetch.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  invalidateApAging(queryClient, organizationId);
};

export const useCreateAPInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accountsPayable', 'create'],
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: CreateAPInvoiceData }) => {
      // Routed through the Hono backend: create_ap_bill_with_line_items
      // writes header + lines in ONE transaction (the old client-side
      // insert-then-manual-rollback pair is gone) and computes amount_total
      // server-side. created_by is stamped from auth.uid() in the RPC.
      const result = await authedPost<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices`,
        buildAPBillCreateBody(data),
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create AP invoice');
      }
      return { success: true, invoice_id: result.invoice_id };
    },
    onSuccess: (_, { organizationId, data }) => {
      invalidateAPBillCreateCaches(queryClient, organizationId);
      trackEvent('bill_created', { line_count: data.line_items.length });

      toast({
        title: "AP Invoice created",
        description: "The accounts payable invoice has been created successfully.",
      });
    },
    onError: (error) => {
      // A coded tax-rate refusal gets its translated message (the form's catch
      // then skips its generic toast — lib/taxRateRefusal.ts).
      if (toastTaxRateRefusal(error, i18n.t)) return;
      toast({
        title: "Error creating AP invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Process an AP invoice payment.
 *
 * Pattern C: the `process_ap_payment` RPC asserts `verify_backend_token()`
 * (migration 20260519003153). Direct `supabase.rpc(...)` calls fail; we route
 * through the Hono backend's POST /api/v1/accounts-payable/orgs/:orgId/:invoiceId/payment.
 *
 * Sentry strategy mirrors `useLinkToAccountingImport` /
 * `useClearingActions` — mutationKey + Sentry.withScope tags so the event is
 * searchable in Sentry by `feature: ap-payment`.
 */
export const useProcessAPPayment = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accounts-payable', 'process-payment'],
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: ProcessAPPaymentData }) => {
      addBreadcrumb('payments.ap.process: started', {
        apInvoiceId: data.ap_invoice_id,
        organizationId,
      });
      const res = await api.api.v1['accounts-payable'].orgs[':orgId'][':invoiceId'].payment.$post({
        param: { orgId: organizationId, invoiceId: data.ap_invoice_id },
        json: {
          payment_amount: data.payment_amount,
          payment_date: data.payment_date,
          payment_method: data.payment_method,
          bank_account_id: data.bank_account_id,
          ap_account_id: data.ap_account_id,
          reference: data.reference ?? null,
          project_id: data.project_id ?? null,
          fund_id: data.fund_id ?? null,
        },
      });
      const result = await unwrap<APInvoiceMutationResult>(res);
      addBreadcrumb('payments.ap.process: succeeded', {
        apInvoiceId: data.ap_invoice_id,
      });
      return result;
    },
    onSuccess: (_, { organizationId, data }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Per-account register (bank overview) — a payment/reversal/delete posts to
      // (or removes journals from) accounts, so the register's rows + running
      // balance must refetch.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // The bill detail backs the expansion panel's payment history and the
      // payment form's remaining balance — a new payment must appear without
      // collapsing/re-expanding the row.
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, data.ap_invoice_id] });
      invalidateApAging(queryClient, organizationId);

      toast({
        title: "AP Payment processed",
        description: "The accounts payable payment has been processed successfully.",
      });
    },
    onError: (error, { organizationId, data }) => {
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'payments.ap');
        scope.setExtra('organizationId', organizationId);
        scope.setExtra('apInvoiceId', data.ap_invoice_id);
        Sentry.captureException(error);
      });
      toast({
        title: "Error processing AP payment",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/** Success payload of `POST …/ap-invoices/:invoiceId/undo-payment` (`reverse_ap_payment`). */
export interface UndoAPPaymentResult {
  success: boolean;
  reversal_transaction_id: string;
  reversal_transaction_number: string;
  /** `YYYY-MM-DD` — the original payment date, or today if that date is in a filed VAT return. */
  reversal_date: string;
}

/**
 * Undo a recorded AP payment by REVERSAL (`reverse_ap_payment`).
 *
 * Posts a reversing journal (the payment stays; a mirror journal cancels it),
 * dated on the original payment date — or today when that date falls in a
 * filed VAT return (`20261030170000`, the AR twin's rule) — so the bill's
 * `amount_paid` recomputes downward and it returns to unpaid / partially paid, while the audit trail is preserved. This is the prerequisite
 * for editing a paid bill: `void_ap_invoice` refuses `reset_to_pending` while
 * `amount_paid > 0`, and `delete_ap_invoice` refuses a bill with a live payment.
 *
 * Manual payments only. An import-linked payment is refused server-side with
 * `error_code = IMPORT_LINKED` (mapped to a "undo from the bank feed" toast);
 * those are undone from the bank-feed clearing.
 *
 * Pattern C: the RPC asserts `verify_backend_token()`, so it is routed through
 * the Hono backend rather than called directly.
 */
export const useUndoAPPayment = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accounts-payable', 'undo-payment'],
    // A bank-feed payment's refusal is a workflow rule, not a failure:
    // App.tsx's MutationCache.onError skips Sentry for it and captures
    // everything else.
    meta: { expectedErrorCodes: ['IMPORT_LINKED'] },
    mutationFn: async ({ organizationId, invoiceId, paymentTransactionId }: {
      organizationId: string;
      invoiceId: string;
      paymentTransactionId: string;
    }) => {
      addBreadcrumb('payments.ap.undo: started', {
        apInvoiceId: invoiceId,
        paymentTransactionId,
        organizationId,
      });
      const result = await authedPost<UndoAPPaymentResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/undo-payment`,
        { payment_transaction_id: paymentTransactionId },
      );
      addBreadcrumb('payments.ap.undo: succeeded', { apInvoiceId: invoiceId });
      return result;
    },
    onSuccess: (result, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Per-account register (bank overview) — a payment/reversal/delete posts to
      // (or removes journals from) accounts, so the register's rows + running
      // balance must refetch.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // The bill detail query backs the payment form's remaining balance and
      // the payment-history panels — stale here means offering to pay an
      // amount that no longer applies.
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      // Reversal posts a journal that touches the bank account, so bank-feed
      // "unmatched" candidates and balances can shift. (Import-linked payments
      // are refused, so we never clear a match row here — but the reversing
      // bank entry still affects these lists.)
      void queryClient.invalidateQueries({ queryKey: ['accountingImports', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['importDetails'] });
      void queryClient.invalidateQueries({ queryKey: ['unpaidEntitiesForImport', organizationId] });
      invalidateApAging(queryClient, organizationId);

      toast({
        title: i18n.t('billing.undoPayment.successTitle'),
        description: i18n.t('billing.undoPayment.successDescription', {
          transactionNumber: result.reversal_transaction_number,
          date: safeFormatDate(result.reversal_date, 'MMM dd, yyyy', result.reversal_date),
        }),
      });
    },
    onError: (error) => {
      // Refusing an import-linked payment is a workflow rule, not a failure —
      // show the "undo from the bank feed" guidance. The api answers it as
      // `{ error: <code>, code: <code> }`; `error.message` is the fallback.
      const code = (error as EnvelopeError<{ code?: string }>).envelope?.code ?? error.message;
      if (code === 'IMPORT_LINKED') {
        toast({
          title: i18n.t('billing.undoPayment.importLinkedTitle'),
          description: i18n.t('billing.undoPayment.importLinkedDescription'),
        });
        return;
      }
      // No explicit capture: App.tsx's MutationCache.onError reports it.
      toast({
        title: i18n.t('billing.undoPayment.errorTitle'),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Move a paid bill's payments onto a new AP account/fund
 * (`repoint_ap_bill_payments`).
 *
 * For each live manual payment on the bill, posts an audit-preserving reversal
 * and an identical replacement on the new account/fund, so a paid bill's
 * liability account/fund can be changed without stranding the money. Used by
 * the edit flow — called AFTER voiding the old posting and BEFORE re-approving,
 * so approve_ap_invoice's PAYMENT_ACCOUNT_MISMATCH backstop passes.
 *
 * Manual payments only: an import-linked payment is refused server-side with
 * `error_code = IMPORT_LINKED`. Pattern C (verify_backend_token), so routed
 * through the Hono backend. Errors surface via the caller's composite handler
 * (the edit is mid-flight), so this hook stays quiet beyond Sentry.
 */
export const useRepointAPPayments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accounts-payable', 'repoint-payments'],
    mutationFn: async ({ organizationId, invoiceId, newApAccountId, newFundId }: {
      organizationId: string;
      invoiceId: string;
      newApAccountId: string;
      newFundId?: string | null;
    }) => {
      addBreadcrumb('payments.ap.repoint: started', { apInvoiceId: invoiceId, organizationId });
      const result = await authedPost<{ success: boolean; payments_moved?: number }>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/repoint-payments`,
        { new_ap_account_id: newApAccountId, new_fund_id: newFundId ?? null },
      );
      addBreadcrumb('payments.ap.repoint: succeeded', { apInvoiceId: invoiceId });
      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);
    },
    onError: (error, { organizationId, invoiceId }) => {
      // IMPORT_LINKED is a workflow rule, not a failure — the caller shows the
      // "undo from the bank feed" guidance. Everything else goes to Sentry.
      if (error.message === 'IMPORT_LINKED') return;
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'payments.ap');
        scope.setExtra('organizationId', organizationId);
        scope.setExtra('apInvoiceId', invoiceId);
        Sentry.captureException(error);
      });
    },
  });
};

export const useApproveAPInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    // A PAYMENT_ACCOUNT_MISMATCH refusal is a rule with a resolution dialog,
    // not a bug: App.tsx's MutationCache.onError keeps Sentry quiet for it.
    meta: { expectedErrorCodes: [PAYMENT_ACCOUNT_MISMATCH] as readonly string[] },
    mutationFn: async ({ organizationId, invoiceId, notes }: {
      organizationId: string;
      invoiceId: string;
      notes?: string;
      /**
       * Suppress this hook's own error toast. Set by composite callers (the
       * edit flow's re-approval / move-payments orchestration) that render their
       * own failure message — otherwise a failed step shows both the generic
       * "Error approving invoice" toast AND the caller's specific one.
       */
      silent?: boolean;
    }) => {
      // SCRUM-184: route through the Hono backend — approve_ap_invoice posts the ledger.
      const result = await authedPost<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/approve`,
        { notes: notes ?? null },
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to approve AP invoice');
      }

      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable'] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions'] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance'] });
      // Per-account register (bank overview) — approving posts the bill journal.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance'] });
      // The bill detail backs the expansion panel — approval must reveal the
      // newly posted journal without collapsing/re-expanding the row.
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);
      // Approval graduates the bill's pre-approval carbon rows (is_draft
      // flips, transaction linkage lands) — the Impact slim index must
      // refetch (useSustainabilityActivityIndex contract).
      invalidateCarbonCaches(queryClient, organizationId);

      toast({
        title: "Invoice Approved",
        description: "The invoice has been approved and ledger entries created.",
      });
    },
    onError: (error, variables) => {
      if (variables?.silent) return; // composite caller owns the failure message
      // The payables tab opens APApproveMismatchDialog for this one — a toast
      // that repeats the refusal beside it would be noise.
      if (isPaymentAccountMismatch(error)) return;
      toast({
        title: "Error approving invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useRejectAPInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, invoiceId, reason }: { organizationId: string; invoiceId: string; reason: string }) => {
      // SCRUM-184: route through the Hono backend for a consistent AP write surface.
      const result = await authedPost<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/reject`,
        { reason },
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to reject AP invoice');
      }

      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable'] });
      // The panel's rejection-details section reads the detail query.
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);

      toast({
        title: "Invoice Rejected",
        description: "The invoice has been rejected.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error rejecting invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteAPInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, invoiceId }: { organizationId: string; invoiceId: string }) => {
      // SCRUM-184: route through the Hono backend — delete_ap_invoice reverses the ledger.
      const result = await authedDelete<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}`,
      );

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to delete AP invoice');
      }

      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Per-account register (bank overview) — a payment/reversal/delete posts to
      // (or removes journals from) accounts, so the register's rows + running
      // balance must refetch.
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // Drop (don't invalidate) the deleted bill's detail cache — the expanded
      // panel is still mounted until the index refetch removes the row, and an
      // invalidate would refetch a guaranteed 404 ("Invoice not found").
      queryClient.removeQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);
      // Deleting the bill cascades its carbon rows (cer.ap_invoice_id) out
      // of the Impact slim index (useSustainabilityActivityIndex contract).
      invalidateCarbonCaches(queryClient, organizationId);

      toast({
        title: "AP Invoice deleted",
        description: "The accounts payable invoice has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error deleting AP invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useCancelAPInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, invoiceId, reason }: { organizationId: string; invoiceId: string; reason: string }) => {
      // Routed through the Hono backend — update_ap_bill_status reproduces the
      // old direct update byte-for-byte (status flip + reason + cancelled_at;
      // NO ledger effect — switching cancel onto the reversing
      // cancel_ap_invoice RPC is a tracked follow-up).
      const result = await authedPatch<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/status`,
        { status: 'cancelled', reason },
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to cancel AP invoice');
      }
      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable'] });
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);

      toast({
        title: "Invoice Cancelled",
        description: "The invoice has been cancelled successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error cancelling invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const usePutAPInvoiceOnHold = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, invoiceId }: { organizationId: string; invoiceId: string; reason?: string }) => {
      const result = await authedPatch<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/status`,
        { status: 'on_hold' },
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to put AP invoice on hold');
      }
      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable'] });
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);

      toast({
        title: "Invoice Put on Hold",
        description: "The invoice has been put on hold.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error putting invoice on hold",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useResumeAPInvoiceFromHold = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, invoiceId }: { organizationId: string; invoiceId: string }) => {
      const result = await authedPatch<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/status`,
        { status: 'pending' },
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to resume AP invoice');
      }
      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable'] });
      void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
      invalidateApAging(queryClient, organizationId);

      toast({
        title: "Invoice Resumed",
        description: "The invoice has been resumed and is now pending approval.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error resuming invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Update payload — FULL EXPLICIT REPLACE (updateAPBillSchema's contract):
 * the required fields must always be sent, and the optional ones are
 * normalized to explicit nulls by the hook — an omitted clearable field is a
 * CLEAR, not a "don't touch". CreateAPInvoiceForm always submits the whole
 * form state, so this matches what actually reaches the hook; the deliberate
 * visible change is that clearing memo/reference/project/fund now sticks.
 */
interface UpdateAPInvoiceData {
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  /** Accepted, never transmitted — the RPC computes it (see create). */
  amount_total?: number;
  description?: string;
  purchase_order_number?: string;
  business_id?: string;
  person_id?: string;
  project_id?: string;
  ap_account_id: string;
  fund_id?: string;
  line_items: Array<{
    expense_account_id: string;
    description: string;
    quantity: number;
    unit_price: number;
    tax_rate: number;
    tax_rate_id?: string;
    tax_treatment?: TaxTreatment;
    tax_inclusive?: boolean;
    sort_order: number;
    exemption_reason?: string;
    exemption_reason_other?: string;
  }>;
}

/**
 * Cache fan-out after a bill's lines are rewritten — shared by the bill
 * update and the grid's line-account edit (useUpdateDocumentLineAccount).
 */
export function invalidateAPBillUpdateCaches(
  queryClient: QueryClient,
  organizationId: string,
  invoiceId: string,
) {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['accountsPayable'] });
  void queryClient.invalidateQueries({ queryKey: ['apInvoice', organizationId, invoiceId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions'] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance'] });
  // Per-account register (bank overview) — a force-edit re-posts the journal.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance'] });
  // Account/amount changes can shift the linked transaction's calculated
  // emissions; refresh dashboards that read from the carbon caches.
  // …and the Impact slim index shows the bill journal's description/date on
  // its carbon rows (useSustainabilityActivityIndex contract).
  invalidateCarbonCaches(queryClient, organizationId);
  invalidateApAging(queryClient, organizationId);
}

export const useUpdateAPInvoice = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['accountsPayable', 'update'],
    mutationFn: async ({ organizationId, invoiceId, data, forceUpdate = false, allowAccountChange = false }: {
      organizationId: string;
      invoiceId: string;
      data: UpdateAPInvoiceData;
      /** If true, allows editing non-pending invoices by voiding ledger entries and resetting to pending */
      forceUpdate?: boolean;
      /**
       * If true, skip the PAYMENT_ACCOUNT_CHANGE pre-flight so a paid bill's
       * AP account/fund CAN change. The caller (the "move payments" flow) is
       * responsible for migrating the payments to the new account
       * (repoint_ap_bill_payments) before re-approving.
       */
      allowAccountChange?: boolean;
      /**
       * Suppress this hook's own error toast — set by composite callers that
       * render their own failure message (the re-approval / move-payments
       * orchestration), so a failed update doesn't double-toast.
       */
      silent?: boolean;
    }) => {
      // Pre-flight: the force-edit gates' six scalars via the embed-free
      // edit-context endpoint (the last direct accounts_payable read-for-write
      // moved behind it). metadata arrives narrowed with '' already coalesced
      // to null server-side.
      const { bill: existingInvoice } = await authedGet<APBillEditContextResponse>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/edit-context`,
      );

      // Check if editing is allowed
      const isNonPending = existingInvoice.approval_status !== 'pending';

      // Editing a bill that already has payments is allowed: the force-edit
      // voids only the posting and re-approves, and the payment — which binds to
      // the bill by ap_invoice_id, not to the posting journal — survives with
      // amount_paid preserved. The ONE unsafe edit is moving the AP control
      // account or the fund: the existing payment sits on the OLD account, so
      // re-approving to a new one would strand it (it would read 'paid' while
      // the money sits elsewhere). Block just that change here with a clean
      // sentinel the form maps to the "move payments to the new account" offer;
      // approve_ap_invoice enforces the same invariant authoritatively
      // server-side (PAYMENT_ACCOUNT_MISMATCH).
      if (isNonPending && (existingInvoice.amount_paid ?? 0) > 0 && !allowAccountChange) {
        const existingMeta = existingInvoice.metadata;
        const norm = (v?: string | null) => v || null;
        const apAccountChanged =
          data.ap_account_id !== undefined &&
          norm(data.ap_account_id) !== norm(existingMeta.ap_account_id);
        // No `!== undefined` guard on the fund: buildInvoiceData collapses a
        // CLEARED fund to `undefined`, and the update rebuilds metadata without
        // it (so `fund_id` is stored NULL). Guarding on `!== undefined` would
        // miss that clear — the liability would re-post in fund NULL while the
        // payment stayed in the old fund, with no error or reconciliation flag.
        // norm() already maps undefined→null, so this detects a clear correctly.
        const fundChanged = norm(data.fund_id) !== norm(existingMeta.fund_id);
        if (apAccountChanged || fundChanged) {
          throw new Error(`PAYMENT_ACCOUNT_CHANGE:${existingInvoice.amount_paid}`);
        }
      }

      if (isNonPending && !forceUpdate) {
        // Return a special error that the UI can detect
        const error = new Error(`REQUIRES_CONFIRMATION:${existingInvoice.approval_status}`);
        throw error;
      }

      // If forceUpdate is true and invoice is not pending, void ledger entries and reset status atomically
      if (isNonPending && forceUpdate) {
        // Call the void RPC with reset_to_pending flag for atomic operation
        // This prevents race conditions where void succeeds but status reset fails.
        // SCRUM-184: routed through the Hono backend — void_ap_invoice reverses the ledger.
        // The endpoint returns null when there's no ledger transaction to void,
        // which we treat as a successful no-op — only throw when the RPC
        // explicitly reports `success: false` (authedPost throws on HTTP errors).
        const voidResult = await authedPost<APInvoiceMutationResult | null>(
          `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}/void`,
          { void_reason: 'Invoice edited - requires re-approval', reset_to_pending: true },
        );
        if (voidResult && !voidResult.success) {
          throw new Error(voidResult.error || 'Failed to void ledger entries');
        }
        // Status reset is now handled atomically by the RPC function
      }

      // Header + line replacement in ONE server-side transaction
      // (update_ap_bill_with_line_items) — the old client-side
      // delete-then-insert-then-update sequence could leave a half-edited
      // bill on a mid-flight failure.
      const result = await authedPatch<APInvoiceMutationResult>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}`,
        {
          invoice_number: data.invoice_number,
          invoice_date: data.invoice_date,
          due_date: data.due_date,
          description: data.description || null,
          ap_account_id: data.ap_account_id,
          line_items: data.line_items.map(toLineItemBody),
          business_id: data.business_id || null,
          person_id: data.person_id || null,
          project_id: data.project_id || null,
          fund_id: data.fund_id || null,
          purchase_order_number: data.purchase_order_number || null,
        },
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to update AP invoice');
      }
      return result;
    },
    onSuccess: (_, { organizationId, invoiceId }) => {
      invalidateAPBillUpdateCaches(queryClient, organizationId, invoiceId);

      toast({
        title: "Invoice Updated",
        description: "The invoice has been updated successfully.",
      });
    },
    onError: (error, variables) => {
      // Composite callers (re-approval / move-payments) show their own failure
      // message; suppress the generic toast so a failed step doesn't double-toast.
      if (variables?.silent) return;
      // Control-flow sentinels, not failures: the caller (CreateAPInvoiceForm)
      // catches these and shows its own translated message — the re-approval
      // confirmation for REQUIRES_CONFIRMATION, the "move payments" offer for
      // PAYMENT_ACCOUNT_CHANGE. Toasting here too would surface the raw,
      // untranslated sentinel string alongside it.
      if (/^(PAYMENT_ACCOUNT_CHANGE|REQUIRES_CONFIRMATION):/.test(error.message)) return;
      if (toastTaxRateRefusal(error, i18n.t)) return;
      toast({
        title: "Error updating invoice",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Lazy per-bill detail — the expansion panel, payment form, invoice view and
 * the edit dialog all read this. The endpoint returns the RAW
 * payment_transactions; `toAPInvoice` derives `voided_payments` from the raw
 * array and THEN narrows to live payments (order is the whole trick — see
 * the APInvoice.voided_payments JSDoc).
 */
export const useAPInvoiceById = (organizationId?: string, invoiceId?: string) => {
  return useQuery({
    queryKey: ['apInvoice', organizationId, invoiceId],
    queryFn: async () => {
      if (!organizationId || !invoiceId) return null;
      const result = await authedGet<APBillDetailResponse>(
        `/api/v1/ap-invoices/orgs/${organizationId}/ap-invoices/${invoiceId}`,
      );
      return toAPInvoice(result.invoice);
    },
    enabled: !!organizationId && !!invoiceId,
    // The 4-embed detail read is heavy and its data only changes through
    // mutations — which all invalidate ['apInvoice', ...] explicitly. Without
    // this, the TanStack defaults refetch on every window focus/remount (once
    // per expanded row), and each refetch mints a new object identity that
    // the edit form's hydration effect would otherwise see (CLAUDE.md:
    // staleTime 2-5 min on reference-ish reads).
    staleTime: 2 * 60 * 1000,
  });
};
